import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  CheckCircle, 
  Settings, 
  User, 
  LogOut, 
  Menu, 
  X,
  Image
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize sidebar state from localStorage, default to false (collapsed)
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarOpen');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Save sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarOpen', JSON.stringify(sidebarOpen));
  }, [sidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: [] },
    { path: '/submissions', label: 'Submissions', icon: FileText, roles: ['Marketing Executive', 'Marketing Head', 'Creative Agency', 'Admin'] },
    { path: '/agency-review', label: 'Review Queue', icon: CheckCircle, roles: ['Creative Agency', 'Admin'] },
    { path: '/approvals', label: 'Approvals', icon: CheckCircle, roles: ['Marketing Head', 'Admin'] },
    { path: '/posts', label: 'Posts', icon: Image, roles: ['Marketing Executive', 'Marketing Head', 'Creative Agency', 'Admin'] },
    { path: '/admin', label: 'Admin Panel', icon: Settings, roles: ['Admin'] },
  ];

  const visibleMenuItems = menuItems.filter(
    (item) => item.roles.length === 0 || item.roles.some((role) => hasRole(role))
  );

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden" data-testid="layout-container">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-slate-800 text-white z-30 px-4 py-3 flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-wider" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          RelayMark
        </h1>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-slate-700 transition"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          bg-gradient-to-b from-slate-800 via-slate-900 to-slate-800 text-white
          fixed lg:static inset-y-0 left-0 z-50
          transform transition-transform duration-300
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${sidebarOpen ? 'w-64' : 'w-64 lg:w-20'}
        `}
        data-testid="sidebar"
      >
        <div className="hidden lg:flex items-center justify-between p-4 border-b border-slate-700">
          {sidebarOpen && (
            <h1 className="text-2xl font-bold tracking-wider" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              RelayMark
            </h1>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-slate-700 transition"
            data-testid="sidebar-toggle-btn"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="mt-6 lg:mt-6 pt-16 lg:pt-0">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center px-4 py-3 mx-2 rounded-lg transition ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-slate-700 hover:text-white'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon size={20} />
                <span className="ml-3 lg:hidden lg:group-hover:block">{item.label}</span>
                {sidebarOpen && <span className="hidden lg:block ml-3">{item.label}</span>}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden pt-14 lg:pt-0">
        {/* Header */}
        <header className="hidden lg:block bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-4 lg:px-6 py-4">
            <div>
              <h2 className="text-lg lg:text-xl font-semibold text-gray-800" style={{ fontFamily: 'Inter, sans-serif' }}>
                {menuItems.find((item) => item.path === location.pathname)?.label || 'Dashboard'}
              </h2>
            </div>
            <div className="flex items-center space-x-2 lg:space-x-4">
              <div className="flex items-center space-x-2 lg:space-x-3" data-testid="user-info">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-gray-800">{user?.name}</p>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {user?.roles.map((role) => (
                      <span
                        key={role}
                        className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                  {user?.name.charAt(0).toUpperCase()}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-red-600 transition"
                data-testid="logout-btn"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-3 sm:p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;